import { DIAGNOSTICS_CONSTANTS } from '@/utils/diagnostics/diagnostics.constants';

const MAX_DEPTH = 6;

function sanitizeLogDataInternal(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === 'string') {
    if (value.length <= DIAGNOSTICS_CONSTANTS.STRING_TRUNCATE) {
      return value;
    }
    return `${value.slice(0, DIAGNOSTICS_CONSTANTS.STRING_TRUNCATE)}…[truncated]`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'symbol' || typeof value === 'function') {
    return String(value);
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeLogDataInternal(value.message, depth + 1, seen),
      stack:
        typeof value.stack === 'string'
          ? sanitizeLogDataInternal(value.stack, depth + 1, seen)
          : undefined,
    };
  }
  if (depth >= MAX_DEPTH) {
    return '[max-depth]';
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return '[circular]';
    }
    seen.add(value);
    return value.slice(0, 50).map((item) => sanitizeLogDataInternal(item, depth + 1, seen));
  }
  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[circular]';
    }
    seen.add(value);
    const result: Record<string, unknown> = {};
    let entries: Array<[string, unknown]> = [];
    try {
      entries = Object.entries(value as Record<string, unknown>).slice(0, 50);
    } catch {
      return '[unenumerable]';
    }
    for (const [key, entryValue] of entries) {
      if (key === 'base64' || key === 'base64Data' || key === 'dataUrl') {
        result[key] = '[omitted]';
        continue;
      }
      result[key] = sanitizeLogDataInternal(entryValue, depth + 1, seen);
    }
    return result;
  }
  return String(value);
}

export function sanitizeLogData(value: unknown, depth = 0): unknown {
  try {
    return sanitizeLogDataInternal(value, depth, new WeakSet<object>());
  } catch {
    return '[sanitize-failed]';
  }
}

export function estimateSerializedSize(value: unknown): number {
  try {
    return JSON.stringify(value).length;
  } catch {
    return DIAGNOSTICS_CONSTANTS.STRING_TRUNCATE;
  }
}
